import * as React from 'react'
import SignaturePad from 'signature_pad'
import { cn } from '../lib/utils'

/** Größtes Bild, das der Server annimmt (AP-46 9.). */
export const UNTERSCHRIFT_MAX_BREITE = 1200
export const UNTERSCHRIFT_MAX_HOEHE = 400

export interface SignatureFieldProps {
  /** Das fertige Bild als PNG — `null`, solange die Spur leer oder zu kurz ist. */
  onChange: (png: Blob | null) => void
  /** Beschriftung unter der Linie („Unterschrift"). */
  label?: string
  loeschenLabel?: string
  /** Hinweis, wenn die Spur zu kurz ist. */
  zuKurzLabel?: string
  disabled?: boolean
  className?: string
}

/** Punkte und Ausdehnung der Spur — eine leere oder zu kurze wird nicht angenommen. */
export function spurReicht(gruppen: { points: { x: number; y: number }[] }[], minPunkte = 8, minAusdehnung = 30): boolean {
  const punkte = gruppen.flatMap((g) => g.points)
  if (punkte.length < minPunkte) return false
  const xs = punkte.map((p) => p.x)
  const ys = punkte.map((p) => p.y)
  return Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)) >= minAusdehnung
}

/** Die Fläche als PNG mit weißem Grund, verkleinert auf höchstens 1200 × 400 px. */
export function alsPng(quelle: HTMLCanvasElement): Promise<Blob | null> {
  const faktor = Math.min(1, UNTERSCHRIFT_MAX_BREITE / quelle.width, UNTERSCHRIFT_MAX_HOEHE / quelle.height)
  const ziel = document.createElement('canvas')
  ziel.width = Math.max(1, Math.round(quelle.width * faktor))
  ziel.height = Math.max(1, Math.round(quelle.height * faktor))
  const ctx = ziel.getContext('2d')
  if (!ctx) return Promise.resolve(null)
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, ziel.width, ziel.height)
  ctx.drawImage(quelle, 0, 0, ziel.width, ziel.height)
  return new Promise((r) => ziel.toBlob(r, 'image/png'))
}

/**
 * AP-46 9. — **Die Unterschriftsfläche**, für PWA und ERP (dort etwa am Tablet im Browser).
 *
 * Gezeichnet wird mit `signature_pad` auf Pointer-Ereignissen; „Löschen" setzt neu; eine leere oder zu
 * kurze Spur wird nicht angenommen (`onChange(null)`). Das Bild geht als PNG mit weißem Grund hinaus,
 * höchstens 1200 × 400 px. Die Fläche hält die Spur beim Drehen des Geräts (BD-42): Sie wird beim
 * Größenwechsel aus den Punkten neu gezeichnet, nicht geleert.
 */
export function SignatureField({ onChange, label = 'Unterschrift', loeschenLabel = 'Löschen', zuKurzLabel = 'Bitte vollständig unterschreiben.', disabled, className }: SignatureFieldProps) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const padRef = React.useRef<SignaturePad | null>(null)
  const [zuKurz, setZuKurz] = React.useState(false)
  const onChangeRef = React.useRef(onChange)
  onChangeRef.current = onChange

  const melde = React.useCallback(async () => {
    const pad = padRef.current
    const canvas = canvasRef.current
    if (!pad || !canvas) return
    if (pad.isEmpty()) {
      setZuKurz(false)
      onChangeRef.current(null)
      return
    }
    const ok = spurReicht(pad.toData())
    setZuKurz(!ok)
    onChangeRef.current(ok ? await alsPng(canvas) : null)
  }, [])

  React.useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const pad = new SignaturePad(canvas, { backgroundColor: 'rgb(255,255,255)', penColor: '#111827', minWidth: 1, maxWidth: 2.8 })
    padRef.current = pad
    const anpassen = () => {
      const daten = pad.toData()
      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      canvas.width = Math.max(1, canvas.offsetWidth * ratio)
      canvas.height = Math.max(1, canvas.offsetHeight * ratio)
      canvas.getContext('2d')?.scale(ratio, ratio)
      pad.clear()
      if (daten.length) pad.fromData(daten)
    }
    anpassen()
    const ende = () => void melde()
    pad.addEventListener('endStroke', ende)
    window.addEventListener('resize', anpassen)
    return () => {
      pad.removeEventListener('endStroke', ende)
      window.removeEventListener('resize', anpassen)
      pad.off()
      padRef.current = null
    }
  }, [melde])

  React.useEffect(() => {
    const pad = padRef.current
    if (!pad) return
    if (disabled) pad.off()
    else pad.on()
  }, [disabled])

  return (
    <div className={cn('space-y-2', className)}>
      <div className="relative rounded-lg border-2 border-dashed border-input bg-white">
        <canvas ref={canvasRef} className="block h-48 w-full touch-none rounded-lg sm:h-56" aria-label={label} />
        <div className="pointer-events-none absolute inset-x-6 bottom-10 border-b border-gray-400" />
        <span className="pointer-events-none absolute bottom-3 left-6 text-xs text-gray-500">{label}</span>
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-red-700" role="status">
          {zuKurz ? zuKurzLabel : ''}
        </span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            padRef.current?.clear()
            void melde()
          }}
          className="min-h-12 rounded-lg border border-input bg-background px-4 text-sm font-medium disabled:opacity-50"
        >
          {loeschenLabel}
        </button>
      </div>
    </div>
  )
}
